import React, { useState, useEffect, useRef, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import PastilleSourcingAuto from './PastilleSourcingAuto';
import APIService from '../services/api';
import { useModuleApp } from '../services/features';
import '../da-vue.css';
import { ancre } from '../tutoriel/ancres';

/* Le responsable arrive en objet : le serveur compose déjà son nom d'affichage. */
const nomResponsable = (r) => (typeof r === 'string' ? r
  : (r?.nom_affiche || [r?.prenom, r?.nom].filter(Boolean).join(' ') || r?.username || ''));

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} aria-hidden="true" />;

// Filtre métier conservé à l'identique (actifs + AO en cours)
const ACTIVE_PIPELINE = ['besoin_p1', 'besoin_p2', 'propose', 'shortlist', 'gagne'];
const ACTIVE_PREFIXES = ['ao_en_cours_'];
// Archivé = ce que le SERVEUR marque comme tel — jamais déduit d'autre chose.
const estArchive = (b) => b.archive === true || b.archived === true || b.archived_at != null || b.archive_le != null;
const isActive = (b) => !b.statut_pipeline || ACTIVE_PIPELINE.includes(b.statut_pipeline) || ACTIVE_PREFIXES.some(p => b.statut_pipeline.startsWith(p));

// Tons des pastilles (maquette du 29.09) : en cours = corail, prêt à envoyer =
// jaune (une action attend), envoyé / validé = vert, refusé = rouge. Les
// couleurs vivent dans src/da-vue.css ; ici, seulement le ton.
const STATUT = {
  encours: { label: 'En cours', ton: 'corail' },
  pret:    { label: 'Prêt à envoyer', ton: 'jaune' },
  envoye:  { label: 'Envoyé', ton: 'vert' },
  valide:  { label: 'Validé', ton: 'vert' },
  refuse:  { label: 'Refusé', ton: 'rouge' },
};
const PRIORITE = { high: 'Haute', medium: 'Moyenne', low: 'Basse' };

// Ancienneté : 10 jours = on la remarque, 15 jours = alerte (maquette).
const ageInfo = (created_at) => {
  if (!created_at) return null;
  const days = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  return { days, label: `${days} j`, niveau: days >= 15 ? 'alerte' : days >= 10 ? 'attention' : '' };
};

// Progression d'un besoin en quatre étapes, lue sur des faits servis :
// ouvert → candidats positionnés → envoyé au client → gagné.
const ETAPES = ['Ouvert', 'Candidats positionnés', 'Envoyé au client', 'Gagné'];
const nbCandidats = (b) => b._count?.candidats ?? b.nb_candidats ?? 0;
const etapeDe = (b) => {
  if (b.statut === 'valide' || b.statut_pipeline === 'gagne') return 4;
  if (b.statut === 'envoye' || ['propose', 'shortlist'].includes(b.statut_pipeline)) return 3;
  if (nbCandidats(b) > 0 || b.statut === 'pret') return 2;
  return 1;
};

const PastilleStatut = ({ statut }) => {
  const s = STATUT[statut] || { label: statut || '—', ton: 'gris' };
  return <span className="da-pastille" data-ton={s.ton}>{s.label}</span>;
};
const Priorite = ({ niveau }) => (niveau
  ? <span className="da-prio" data-niveau={niveau}><span className="da-prio__point" aria-hidden="true" />{PRIORITE[niveau] || niveau}</span>
  : <span className="da-cellule--sourdine">—</span>);

// ─── Édition inline réutilisable (clic → input) ──────────────────────────────
function useInline(initial, onCommit) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(initial);
  const start = (e) => { e?.stopPropagation(); setVal(initial); setEditing(true); };
  const commit = () => { onCommit(val); setEditing(false); };
  const cancel = () => { setVal(initial); setEditing(false); };
  return { editing, val, setVal, start, commit, cancel };
}

// ─── Micro-visualisations des cartes de chiffres ─────────────────────────────
// Tendance : une seule série (besoins créés par mois), barres fines ancrées à
// la ligne de base, le mois en cours ressorti ; le détail au survol.
function Tendance({ mois }) {
  const max = Math.max(1, ...mois.map(m => m.n));
  const L = 7, E = 3, H = 34;
  const resume = mois.map(m => `${m.libelle} : ${m.n}`).join(', ');
  return (
    <svg className="da-tendance" width={mois.length * (L + E) - E} height={H} viewBox={`0 0 ${mois.length * (L + E) - E} ${H}`}
      role="img" aria-label={`Besoins créés par mois — ${resume}`}>
      {mois.map((m, i) => {
        const h = Math.max(3, Math.round((m.n / max) * H));
        return (
          <rect key={m.cle} x={i * (L + E)} y={H - h} width={L} height={h} rx="2"
            className={i === mois.length - 1 ? 'da-tendance__courant' : 'da-tendance__barre'}>
            <title>{`${m.libelle} : ${m.n} besoin${m.n > 1 ? 's' : ''} créé${m.n > 1 ? 's' : ''}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}
// Jauge : une part contre son total, piste et remplissage du même ton.
function Jauge({ part, libelle }) {
  const r = 13, c = 2 * Math.PI * r, p = Math.max(0, Math.min(100, part));
  return (
    <svg className="da-jauge" width="32" height="32" viewBox="0 0 32 32" role="img" aria-label={`${libelle} : ${p} %`}>
      <title>{`${libelle} : ${p} %`}</title>
      <circle cx="16" cy="16" r={r} className="da-jauge__piste" />
      <circle cx="16" cy="16" r={r} className="da-jauge__part" strokeDasharray={`${(c * p) / 100} ${c}`} transform="rotate(-90 16 16)" />
    </svg>
  );
}

// ─── Menu d'une ligne (« … ») ────────────────────────────────────────────────
function MenuLigne({ besoin, ouvert, onBasculer, onFermer, actions }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e) => { if (ref.current && !ref.current.contains(e.target)) onFermer(); };
    const echap = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    const premier = ref.current?.querySelector('[role="menuitem"]');
    premier?.focus();
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [ouvert, onFermer]);
  return (
    <div className="da-menu" ref={ref} onClick={e => e.stopPropagation()}>
      <button type="button" className="da-kebab" aria-haspopup="menu" aria-expanded={ouvert}
        aria-label={`Actions pour « ${besoin.titre} »`} onClick={onBasculer}>
        <BI icon="three-dots" />
      </button>
      {ouvert && (
        <div className="da-menu__liste" role="menu" aria-label={`Actions pour « ${besoin.titre} »`}
          onKeyDown={e => {
            // Flèches haut / bas entre les entrées, comme tout menu au clavier.
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            e.preventDefault();
            const items = [...e.currentTarget.querySelectorAll('[role="menuitem"]')];
            const i = items.indexOf(document.activeElement);
            items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
          }}>
          {actions.map(a => a.separateur
            ? <div key={a.cle} className="da-menu__filet" role="separator" />
            : (
              <button key={a.cle} type="button" role="menuitem" className={`da-menu__item${a.danger ? ' da-menu__item--danger' : ''}`}
                onClick={() => { onFermer(); a.faire(); }}>
                <BI icon={a.icone} /> {a.libelle}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default function BesoinsList({ onSelectBesoin, onCreateBesoin, onDeleteBesoin, onOuvrirKanban, showNotification }) {
  // Les deux moitiés, lues une fois : le serveur rend les non-archivés par
  // défaut et les archivés avec ?archives=1. Les onglets et leurs compteurs
  // en découlent, sans rien recharger au changement d'onglet.
  const [nonArchives, setNonArchives] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('actifs'); // 'tous' | 'actifs' | 'archives'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'cards'
  const [sort, setSort] = useState('recent');
  const [period, setPeriod] = useState('all'); // 'all' | jours ('30' | '90' | '180') sur created_at
  const [plusFiltres, setPlusFiltres] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLen, setPageLen] = useState(10);
  const [menuOuvert, setMenuOuvert] = useState(null); // id du besoin dont le menu « … » est ouvert
  const fermerMenu = useCallback(() => setMenuOuvert(null), []);
  // Sélection par lot : archiver, désarchiver ou supprimer plusieurs besoins
  // d'un coup. La sélection vit par identifiant, pas par page.
  const [sel, setSel] = useState(() => new Set());
  const [lotBusy, setLotBusy] = useState(false);
  // ── Diffusion sur la marketplace ────────────────────────────────────────
  // Le filtre n'existe que si l'app sourcia est activée pour l'organisation
  // ET pour ce compte (useModuleApp) : proposer un filtre sur une capacité
  // qu'on n'a pas, c'est promettre un résultat qui sera toujours vide.
  const sourciaActif = useModuleApp('sourcia');
  const [diffuseFilter, setDiffuseFilter] = useState(''); // '' | 'oui' | 'non'
  const [besoinsDiffuses, setBesoinsDiffuses] = useState(null); // Set(id) — null = pas encore su
  const plusFiltresRef = useRef(null);
  const toutCocheRef = useRef(null);

  const authHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  // Les besoins ACTUELLEMENT diffusés, lus une fois. Route absente ou en
  // erreur : on garde `null` — le filtre reste masqué plutôt que de trier sur
  // une liste vide et de faire disparaître tous les besoins.
  useEffect(() => {
    if (!sourciaActif) { setBesoinsDiffuses(null); return undefined; }
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/diffusion/besoins`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        const arr = j.data || j.diffusions || (Array.isArray(j) ? j : []);
        if (!annule && Array.isArray(arr)) {
          setBesoinsDiffuses(new Set(arr.map(d => Number(d.besoin_id)).filter(Number.isFinite)));
        }
      } catch { /* l'écran reste utilisable sans le filtre */ }
    })();
    return () => { annule = true; };
  }, [sourciaActif]);
  const filtreDiffusionDispo = sourciaActif && besoinsDiffuses != null;

  const loadBesoins = async () => {
    try {
      setLoading(true);
      const [a, b] = await Promise.all([
        APIService.getBesoins({}),
        APIService.getBesoins({ archives: 1 }).catch(() => ({ besoins: [] })),
      ]);
      // Chaque moitié ne garde que ce qui lui appartient : si la route ignore
      // ?archives=1 et renvoie les actifs, l'onglet Archivés reste vide plutôt
      // que d'afficher des actifs sous ce libellé.
      setNonArchives((a.besoins || []).filter(x => !estArchive(x)));
      setArchives((b.besoins || []).filter(estArchive));
    } catch { showNotification('Erreur chargement des besoins', 'error'); } finally { setLoading(false); }
  };
  useEffect(() => { loadBesoins(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // La sélection ne survit pas au changement d'onglet : on n'agit jamais sur
  // des lignes qu'on ne voit plus.
  useEffect(() => { setSel(new Set()); setMenuOuvert(null); }, [onglet]);

  // « Plus de filtres » se ferme au clic dehors et sur Échap.
  useEffect(() => {
    if (!plusFiltres) return undefined;
    const dehors = (e) => { if (plusFiltresRef.current && !plusFiltresRef.current.contains(e.target)) setPlusFiltres(false); };
    const echap = (e) => { if (e.key === 'Escape') setPlusFiltres(false); };
    document.addEventListener('mousedown', dehors); document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [plusFiltres]);

  const actifs = nonArchives.filter(isActive);
  const besoins = onglet === 'archives' ? archives : onglet === 'tous' ? [...nonArchives, ...archives] : actifs;

  const basculeSel = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selRows = besoins.filter(b => sel.has(b.id));

  // Suppression : un appel par besoin (route unitaire existante), verdict par
  // besoin, bilan honnête — jamais un « tout supprimé » si un seul a échoué.
  const supprimerLot = async () => {
    const n = selRows.length;
    if (!n) return;
    const noms = selRows.slice(0, 5).map(b => `« ${b.titre} »`).join(', ') + (n > 5 ? '…' : '');
    if (!await confirmErpia({ message: `Supprimer ${n} besoin${n > 1 ? 's' : ''} ?\n${noms}\n\nLes candidats positionnés, documents et campagnes de ces besoins ne seront plus accessibles. Il n'y a pas de corbeille — pour garder les données, utilisez plutôt « Archiver ».` })) return;
    setLotBusy(true);
    const echecs = [];
    let faits = 0;
    for (const b of selRows) {
      try { await APIService.deleteBesoin(b.id); faits += 1; }
      catch (e) { echecs.push(`${b.titre} (${e.message || 'erreur'})`); }
    }
    setLotBusy(false);
    setSel(new Set());
    showNotification?.(echecs.length === 0
      ? `${faits} besoin${faits > 1 ? 's' : ''} supprimé${faits > 1 ? 's' : ''}.`
      : `${faits} supprimé${faits > 1 ? 's' : ''}, ${echecs.length} en échec : ${echecs.join(' · ')}`,
      echecs.length ? 'error' : 'success');
    loadBesoins();
  };

  // Archiver / désarchiver : même route pour un besoin (menu « … ») ou un lot.
  // Archiver sort le besoin des listes actives SANS RIEN EFFACER ; tant que la
  // route n'existe pas, on l'annonce et on n'écrit rien.
  const basculerArchive = async (liste, archiver) => {
    const n = liste.length;
    if (!n) return;
    if (archiver && !await confirmErpia({ message: `Archiver ${n} besoin${n > 1 ? 's' : ''} ?\n\nIls sortent des listes actives mais rien n'est effacé : candidats, documents et campagnes restent consultables. Un besoin archivé peut être désarchivé.` })) return;
    setLotBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${archiver ? 'archiver' : 'desarchiver'}`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ besoin_ids: liste.map(b => b.id) }),
      });
      if (r.status === 404 || r.status === 405 || r.status === 501) {
        showNotification?.(archiver
          ? 'L\'archivage des besoins arrive côté serveur — rien n\'a été modifié. En attendant, la suppression reste possible (définitive).'
          : 'Le désarchivage arrive côté serveur — rien n\'a été modifié.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || `${n} besoin${n > 1 ? 's' : ''} ${archiver ? 'archivé' : 'désarchivé'}${n > 1 ? 's' : ''}.`, 'success');
      setSel(new Set());
      loadBesoins();
    } catch (e) { showNotification?.(`${archiver ? 'Archivage' : 'Désarchivage'} impossible : ${e.message}`, 'error'); }
    finally { setLotBusy(false); }
  };

  // Clé de comparaison client : espaces normalisés + minuscules — « FHVI (Sébastien
  // Roche) » et « FHVI (Sébastien Roche)  » ne font plus deux entrées du sélecteur.
  const normClient = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const clientKey = (s) => normClient(s).toLowerCase();

  const patchBesoin = async (id, field, val) => {
    try {
      await fetch(`${BASE}/besoins/${id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ [field]: val }) });
      const maj = (l) => l.map(b => b.id === id ? { ...b, [field]: val } : b);
      setNonArchives(maj); setArchives(maj);
    } catch {}
  };

  const periodCutoff = period === 'all' ? null : Date.now() - Number(period) * 86400000;
  const filtered = besoins.filter(b => {
    if (statusFilter && b.statut !== statusFilter) return false;
    if (periodCutoff && !(b.created_at && new Date(b.created_at).getTime() >= periodCutoff)) return false;
    if (clientFilter && clientKey(b.client) !== clientKey(clientFilter)) return false;
    if (filtreDiffusionDispo && diffuseFilter) {
      const diffuse = besoinsDiffuses.has(Number(b.id));
      if (diffuseFilter === 'oui' && !diffuse) return false;
      if (diffuseFilter === 'non' && diffuse) return false;
    }
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (b.titre || '').toLowerCase().includes(s) || (b.client || '').toLowerCase().includes(s)
      || nomResponsable(b.responsable).toLowerCase().includes(s)
      || (b.reference_interne || '').toLowerCase().includes(s);
  });
  const filtresActifs = [statusFilter, clientFilter, diffuseFilter, period !== 'all' ? period : '', searchTerm].filter(Boolean).length;
  const effacerFiltres = () => { setStatusFilter(''); setClientFilter(''); setDiffuseFilter(''); setPeriod('all'); setSearchTerm(''); };

  // Tri client-side (la liste est déjà chargée), piloté par les en-têtes.
  const PRIO_RANK = { high: 0, medium: 1, low: 2 };
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'old':       return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'prio':      return (PRIO_RANK[a.priorite] ?? 1) - (PRIO_RANK[b.priorite] ?? 1);
      case 'candidats': return nbCandidats(b) - nbCandidats(a);
      case 'titre':     return (a.titre || '').localeCompare(b.titre || '', 'fr');
      case 'client':    return (a.client || '').localeCompare(b.client || '', 'fr');
      case 'etape':     return etapeDe(b) - etapeDe(a);
      default:          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });

  // Pagination : page recalée quand un filtre réduit la liste.
  const pages = Math.max(1, Math.ceil(sorted.length / pageLen));
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, clientFilter, diffuseFilter, sort, period, view, onglet, pageLen]);
  useEffect(() => { if (page > pages) setPage(pages); }, [pages, page]);
  const pageRows = sorted.slice((page - 1) * pageLen, page * pageLen);

  // Case « tout cocher » : tout ce que les filtres montrent, pas seulement la page.
  const toutEstSelectionne = sorted.length > 0 && sorted.every(b => sel.has(b.id));
  const unePartie = !toutEstSelectionne && sorted.some(b => sel.has(b.id));
  useEffect(() => { if (toutCocheRef.current) toutCocheRef.current.indeterminate = unePartie; }, [unePartie]);

  // ── Chiffres (sur les besoins actifs) ──────────────────────────────────
  const stats = {
    total: actifs.length,
    encours: actifs.filter(b => b.statut === 'encours').length,
    pret: actifs.filter(b => b.statut === 'pret').length,
    envoye: actifs.filter(b => b.statut === 'envoye').length,
    totalCandidats: actifs.reduce((s, b) => s + nbCandidats(b), 0),
  };
  // Tendance des créations sur six mois, actifs et archivés confondus : un
  // besoin archivé a bien été ouvert ce mois-là.
  const maintenant = new Date();
  const moisTendance = Array.from({ length: 6 }, (_, k) => {
    const d = new Date(maintenant.getFullYear(), maintenant.getMonth() - (5 - k), 1);
    const fin = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    const n = [...nonArchives, ...archives].filter(b => {
      const t = b.created_at ? new Date(b.created_at).getTime() : NaN;
      return t >= d.getTime() && t < fin.getTime();
    }).length;
    return { cle: `${d.getFullYear()}-${d.getMonth()}`, libelle: d.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' }), n };
  });
  const nouveauxDuMois = moisTendance[moisTendance.length - 1].n;
  const partEnCours = stats.total ? Math.round((100 * stats.encours) / stats.total) : 0;
  const parBesoin = stats.total ? stats.totalCandidats / stats.total : 0;

  // Sélecteur clients dédupliqué (variantes d'espaces/casse fusionnées — la
  // première graphie rencontrée sert d'étiquette), tri alphabétique français.
  const clients = (() => {
    const m = new Map();
    for (const b of besoins) { const c = normClient(b.client); if (c && !m.has(c.toLowerCase())) m.set(c.toLowerCase(), c); }
    return [...m.values()].sort((a, b) => a.localeCompare(b, 'fr'));
  })();

  const ONGLETS = [['tous', 'Tous', nonArchives.length + archives.length], ['actifs', 'Actifs', actifs.length], ['archives', 'Archivés', archives.length]];

  // En-tête triable : un bouton, et aria-sort sur la colonne. Appelé comme une
  // fonction (pas un composant) : le bouton garde le focus après un tri.
  const enTete = (libelle, cle, cles, className = '') => {
    const actif = cles.includes(sort);
    const suivant = cle === 'depuis' ? (sort === 'recent' ? 'old' : 'recent') : cles[0];
    const sens = actif ? (sort === 'old' ? 'ascending' : 'descending') : 'none';
    return (
      <span role="columnheader" aria-sort={sens} className={className}>
        <button type="button" className={`da-tri${actif ? ' is-actif' : ''}`} onClick={() => setSort(suivant)}
          title={`Trier par ${libelle.toLowerCase()}`}>
          {libelle}<BI icon={actif ? (sort === 'old' ? 'arrow-up' : 'arrow-down') : 'arrow-down-up'} />
        </button>
      </span>
    );
  };

  const archivables = selRows.filter(b => !estArchive(b));
  const desarchivables = selRows.filter(estArchive);

  return (
    // Direction du 29.09 : racine marquée data-da="vue" (règles dans src/da-vue.css).
    <div data-da="vue" style={{ margin: '0 auto', padding: '4px 0 48px' }}>

      {/* En-tête de page */}
      <div className="da-entete">
        <div>
          <nav className="da-fil" aria-label="Fil d'Ariane"><span>Recrutement</span><BI icon="chevron-right" /><span aria-current="page">Besoins</span></nav>
          <h1 className="da-titre">Gestion des besoins</h1>
          <p className="da-sous-titre">Suivi du recrutement, du besoin à l'envoi au client.</p>
        </div>
        <button {...ancre('besoin-creer')} type="button" onClick={onCreateBesoin} className="da-btn da-btn--primaire">
          <BI icon="plus-lg" /> Nouveau besoin
        </button>
      </div>

      {/* Chiffres : la première carte en vert sombre plein, comme la maquette. */}
      <div className="da-kpis">
        <div className="da-kpi da-kpi--fort">
          <div className="da-kpi__haut">
            <span className="da-kpi__libelle">Besoins actifs</span>
            <span className="da-icone" aria-hidden="true"><BI icon="briefcase" /></span>
          </div>
          <div className="da-kpi__bas">
            <span className="da-kpi__valeur">{stats.total}</span>
            <span className="da-kpi__note" title="Besoins créés depuis le 1er du mois">{nouveauxDuMois > 0 ? `+${nouveauxDuMois} ce mois` : 'aucun ce mois'}</span>
            <span className="da-kpi__viz"><Tendance mois={moisTendance} /></span>
          </div>
        </div>
        {/* La jauge PREND LA PLACE de l'icône (02.10 : « trop moche » — sablier,
            anneau et pourcentage corail s'empilaient pour dire une seule chose).
            Même emplacement et même taille que les icônes des cartes voisines ;
            la part se lit en note, en gris : ce n'est pas une alerte. */}
        <div className="da-kpi">
          <div className="da-kpi__haut">
            <span className="da-kpi__libelle">En cours</span>
            {stats.total > 0
              ? <span className="da-kpi__jauge"><Jauge part={partEnCours} libelle="Part des besoins actifs en cours" /></span>
              : <span className="da-icone" aria-hidden="true"><BI icon="hourglass-split" /></span>}
          </div>
          <div className="da-kpi__bas">
            <span className="da-kpi__valeur">{stats.encours}</span>
            {stats.total > 0 && <span className="da-kpi__note da-kpi__note--sourdine">{partEnCours} % des actifs</span>}
          </div>
        </div>
        {[['Prêts à envoyer', stats.pret, 'send'], ['Envoyés', stats.envoye, 'check2-circle']].map(([libelle, valeur, icone]) => (
          <div key={libelle} className="da-kpi">
            <div className="da-kpi__haut">
              <span className="da-kpi__libelle">{libelle}</span>
              <span className="da-icone" aria-hidden="true"><BI icon={icone} /></span>
            </div>
            <div className="da-kpi__bas"><span className="da-kpi__valeur">{valeur}</span></div>
          </div>
        ))}
        <div className="da-kpi">
          <div className="da-kpi__haut">
            <span className="da-kpi__libelle">Candidats</span>
            <span className="da-icone" aria-hidden="true"><BI icon="people" /></span>
          </div>
          <div className="da-kpi__bas">
            <span className="da-kpi__valeur">{stats.totalCandidats}</span>
            {stats.total > 0 && <span className="da-kpi__note da-kpi__note--sourdine">{parBesoin.toLocaleString('fr-CH', { maximumFractionDigits: 1 })} par besoin</span>}
          </div>
        </div>
      </div>

      <section className="da-carte" aria-label="Liste des besoins">
        {/* Onglets (ce qu'on regarde) et vues (comment on le regarde). */}
        <div className="da-carte__entete da-carte__entete--onglets">
          <div className="da-onglets" role="tablist" aria-label="Besoins affichés">
            {ONGLETS.map(([cle, libelle, n]) => (
              <button key={cle} type="button" role="tab" aria-selected={onglet === cle} className="da-onglet" onClick={() => setOnglet(cle)}
                title={cle === 'archives' ? 'Les besoins archivés sortent des listes actives mais gardent toutes leurs données.' : undefined}>
                {libelle}<span className="da-onglet__n">{loading ? '' : n}</span>
              </button>
            ))}
          </div>
          <div className="da-vues" role="group" aria-label="Affichage">
            <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}><BI icon="list-ul" /> Liste</button>
            <button type="button" aria-pressed={view === 'cards'} onClick={() => setView('cards')}><BI icon="grid" /> Cartes</button>
            {onOuvrirKanban && <button type="button" aria-pressed={false} onClick={onOuvrirKanban} title="Ouvrir le pipeline des besoins"><BI icon="kanban" /> Kanban</button>}
          </div>
        </div>

        {/* Barre d'outils */}
        <div className="da-outils">
          <label className="da-recherche">
            <BI icon="search" />
            <input placeholder="Rechercher un besoin, un client, une référence…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              aria-label="Rechercher un besoin" />
          </label>
          <ErpiaSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="encours">En cours</option>
            <option value="pret">Prêt à envoyer</option>
            <option value="envoye">Envoyé</option>
          </ErpiaSelect>
          <ErpiaSelect value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
            <option value="">Tous les clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </ErpiaSelect>
          {filtreDiffusionDispo && (
            <ErpiaSelect value={diffuseFilter} onChange={e => setDiffuseFilter(e.target.value)}
              title="Filtrer selon la diffusion sur sourcia">
              <option value="">Diffusion sourcia</option>
              <option value="oui">Diffusés sur sourcia ({besoinsDiffuses.size})</option>
              <option value="non">Non diffusés</option>
            </ErpiaSelect>
          )}
          <div className="da-plus" ref={plusFiltresRef}>
            <button type="button" className="da-btn da-btn--secondaire" aria-expanded={plusFiltres} aria-haspopup="dialog"
              onClick={() => setPlusFiltres(o => !o)}>
              Plus de filtres{period !== 'all' && <span className="da-plus__n">1</span>}<BI icon="sliders" />
            </button>
            {plusFiltres && (
              <div className="da-plus__panneau" role="dialog" aria-label="Plus de filtres">
                <div className="da-plus__titre">Créés depuis</div>
                <div className="da-segments" role="group" aria-label="Période de création">
                  {[['all', 'Toujours'], ['30', '1 mois'], ['90', '3 mois'], ['180', '6 mois']].map(([v, texte]) => (
                    <button key={v} type="button" aria-pressed={period === v} onClick={() => setPeriod(v)}>{texte}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {filtresActifs > 0 && <button type="button" className="da-lien" onClick={effacerFiltres}>Effacer les filtres</button>}
        </div>

        {/* Barre de lot : visible dès qu'un besoin est coché. L'archivage garde
            tout (candidats, documents, campagnes) ; la suppression est
            définitive — les deux confirmations le disent. */}
        {sel.size > 0 && (
          <div className="da-lot">
            <span className="da-lot__compte">
              {sel.size} besoin{sel.size > 1 ? 's' : ''} sélectionné{sel.size > 1 ? 's' : ''}
            </span>
            <span style={{ flex: 1 }} />
            {archivables.length > 0 && (
              <button type="button" className="da-btn da-btn--secondaire" onClick={() => basculerArchive(archivables, true)} disabled={lotBusy}
                title="Les besoins sortent des listes actives — candidats, documents et campagnes sont conservés.">
                <BI icon={lotBusy ? 'arrow-repeat' : 'archive'} /> Archiver{desarchivables.length > 0 ? ` (${archivables.length})` : ''}
              </button>
            )}
            {desarchivables.length > 0 && (
              <button type="button" className="da-btn da-btn--secondaire" onClick={() => basculerArchive(desarchivables, false)} disabled={lotBusy}
                title="Les besoins retrouvent les listes actives, avec leur statut d'avant.">
                <BI icon={lotBusy ? 'arrow-repeat' : 'box-arrow-up'} /> Désarchiver{archivables.length > 0 ? ` (${desarchivables.length})` : ''}
              </button>
            )}
            <button type="button" className="da-btn da-btn--danger" onClick={supprimerLot} disabled={lotBusy}
              title="Suppression définitive — pour garder les données, utilisez Archiver.">
              <BI icon={lotBusy ? 'arrow-repeat' : 'trash3'} /> Supprimer
            </button>
            <button type="button" className="da-carre" onClick={() => setSel(new Set())} disabled={lotBusy}
              aria-label="Annuler la sélection" title="Annuler la sélection">
              <BI icon="x-lg" style={{ fontSize: '12px' }} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="da-vide" aria-live="polite">
            <BI icon="arrow-repeat" style={{ fontSize: '22px', color: 'var(--erp-da-text-3)', display: 'inline-block', animation: 'ats-spin .8s linear infinite' }} />
            <div className="da-vide__texte">Chargement des besoins…</div>
          </div>
        ) : sorted.length === 0 ? (
          <div className="da-vide">
            <span className="da-icone" aria-hidden="true" style={{ width: '44px', height: '44px', fontSize: '20px' }}><BI icon="inbox" /></span>
            <div className="da-vide__titre">
              {filtresActifs > 0 && besoins.length > 0 ? 'Aucun besoin ne correspond à ces filtres'
                : onglet === 'archives' ? 'Aucun besoin archivé' : 'Aucun besoin'}
            </div>
            <div className="da-vide__texte">
              {filtresActifs > 0 && besoins.length > 0 ? 'Élargissez la recherche ou effacez les filtres.'
                : onglet === 'archives'
                  ? 'Archivez depuis la liste des actifs : cochez des besoins, puis « Archiver ». Si vous venez d\'archiver et que rien n\'apparaît, l\'archivage arrive encore côté serveur.'
                  : 'Créez votre premier besoin pour lancer le recrutement.'}
            </div>
            {filtresActifs > 0 && besoins.length > 0 ? (
              <button type="button" className="da-btn da-btn--secondaire" onClick={effacerFiltres} style={{ marginTop: '16px' }}>Effacer les filtres</button>
            ) : onglet !== 'archives' && (
              <button type="button" className="da-btn da-btn--primaire" onClick={onCreateBesoin} style={{ marginTop: '16px' }}>
                <BI icon="plus-lg" /> Nouveau besoin
              </button>
            )}
          </div>
        ) : view === 'list' ? (
          // ───────── VUE LISTE : le tableau de la maquette ─────────
          <div role="table" aria-label="Besoins">
            <div className="da-grille da-tete" role="row">
              <span role="columnheader">
                <input ref={toutCocheRef} type="checkbox" className="da-coche" checked={toutEstSelectionne}
                  onChange={() => setSel(toutEstSelectionne ? new Set() : new Set(sorted.map(b => b.id)))}
                  aria-label={toutEstSelectionne ? 'Tout désélectionner' : `Sélectionner les ${sorted.length} besoins affichés`} />
              </span>
              {enTete('Besoin', 'titre', ['titre'])}
              {enTete('Client', 'client', ['client'], 'da-col-client')}
              <span role="columnheader">Statut</span>
              <span role="columnheader" className="da-col-sourcing" title="Où en est le sourcing lancé seul à la création du besoin">Sourcing</span>
              {enTete('Priorité', 'prio', ['prio'], 'da-col-prio')}
              {enTete('Candidats', 'candidats', ['candidats'], 'da-col-candidats')}
              {enTete('Depuis', 'depuis', ['recent', 'old'], 'da-col-age')}
              {enTete('Progression', 'etape', ['etape'], 'da-col-progression')}
              <span role="columnheader" aria-label="Actions" />
            </div>
            <div {...ancre('besoins-liste')}>
              {pageRows.map(b => {
                const nb = nbCandidats(b);
                const age = ageInfo(b.created_at);
                const resp = nomResponsable(b.responsable);
                const etape = etapeDe(b);
                const archive = estArchive(b);
                return (
                  <div key={b.id} className="da-grille da-ligne" role="row" aria-selected={sel.has(b.id)} data-statut={b.statut || ''}
                    onClick={() => onSelectBesoin(b)}>
                    <span role="cell">
                      <input type="checkbox" className="da-coche" checked={sel.has(b.id)} onChange={() => basculeSel(b.id)}
                        onClick={e => e.stopPropagation()} aria-label={`Sélectionner « ${b.titre} »`}
                        title="Sélectionner pour archiver ou supprimer par lot" />
                    </span>
                    <div role="cell" className="da-objet">
                      <span className="da-icone da-icone--corail" aria-hidden="true"><BI icon="briefcase" style={{ fontSize: '14px' }} /></span>
                      <div className="da-objet__texte">
                        <div className="da-objet__titre" title={b.titre}>{b.titre}</div>
                        <div className="da-objet__sous">
                          <span>{resp || 'Commercial à attribuer'}</span>
                          {/* Qui recrute (14.10) — servi par GET /besoins, absent d'un serveur ancien. */}
                          {b.recruteuse?.nom && <span className="da-objet__recrut" title={`Recrutement : ${b.recruteuse.nom}`}><BI icon="person-check" /> {b.recruteuse.nom}</span>}
                          {/* Référence interne (27.10) — pour l'équipe, jamais publiée. */}
                          {b.reference_interne && <span className="da-objet__recrut ats-mono" title={`Référence interne : ${b.reference_interne}`}><BI icon="hash" /> {b.reference_interne}</span>}
                          {archive && <span className="da-pastille" data-ton="gris"><BI icon="archive" /> Archivé</span>}
                        </div>
                      </div>
                    </div>
                    <span role="cell" className="da-cellule da-col-client" title={b.client || ''}>{b.client || '—'}</span>
                    <span role="cell"><PastilleStatut statut={b.statut} /></span>
                    {/* Sa propre colonne : dans la sous-ligne, la pastille écrasait le
                        commercial et les libellés longs y étaient coupés au milieu d'un mot. */}
                    <span role="cell" className="da-col-sourcing" data-vide={b.sourcing_auto?.pastille ? undefined : ''}>
                      {b.sourcing_auto?.pastille
                        ? <PastilleSourcingAuto sourcingAuto={b.sourcing_auto} />
                        : <span className="da-cellule--sourdine" title="Aucun sourcing lancé à la création (besoin plus ancien, ou lancement coupé)">—</span>}
                    </span>
                    <span role="cell" className="da-col-prio"><Priorite niveau={b.priorite} /></span>
                    <span role="cell" className="da-cellule da-col-candidats" title={`${nb} candidat(s)`}>
                      <BI icon="person" style={{ fontSize: '12px', marginRight: '6px', color: 'var(--erp-da-text-3)' }} />{nb}
                    </span>
                    <span role="cell" className="da-cellule da-col-age">
                      {age ? <span className="da-age" data-niveau={age.niveau} title={`Créé il y a ${age.days} jours`}>{age.label}</span> : '—'}
                    </span>
                    <span role="cell" className="da-col-progression">
                      <span className="da-progression" data-avance={etape >= 3 ? 'oui' : 'non'}
                        title={`Étape ${etape} sur 4 : ${ETAPES[etape - 1]} (${ETAPES.join(' → ')})`}>
                        <span className="da-progression__piste"><span className="da-progression__part" style={{ width: `${(etape / 4) * 100}%` }} /></span>
                        <span className="da-progression__n">{etape}/4</span>
                      </span>
                    </span>
                    <div role="cell" className="da-actions">
                      <MenuLigne besoin={b} ouvert={menuOuvert === b.id}
                        onBasculer={() => setMenuOuvert(o => (o === b.id ? null : b.id))} onFermer={fermerMenu}
                        actions={[
                          { cle: 'ouvrir', libelle: 'Ouvrir le besoin', icone: 'box-arrow-up-right', faire: () => onSelectBesoin(b) },
                          archive
                            ? { cle: 'desarchiver', libelle: 'Désarchiver', icone: 'box-arrow-up', faire: () => basculerArchive([b], false) }
                            : { cle: 'archiver', libelle: 'Archiver', icone: 'archive', faire: () => basculerArchive([b], true) },
                          { cle: 'filet', separateur: true },
                          { cle: 'supprimer', libelle: 'Supprimer', icone: 'trash3', danger: true, faire: () => onDeleteBesoin(b) },
                        ]} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // ───────── VUE CARTES ─────────
          <div className="da-tuiles">
            {pageRows.map(b => (
              <BesoinTuile key={b.id} besoin={b} onSelect={onSelectBesoin} onDelete={onDeleteBesoin} onPatch={patchBesoin} />
            ))}
          </div>
        )}

        {/* Pied : le compte, la pagination et la taille de page. */}
        {!loading && sorted.length > 0 && (() => {
          const from = Math.max(1, page - 2), to = Math.min(pages, page + 2);
          const win = []; for (let p = from; p <= to; p++) win.push(p);
          return (
            <div className="da-pied">
              <span className="da-pied__compte">{sorted.length} besoin{sorted.length > 1 ? 's' : ''}</span>
              <div className="da-pied__pages">
                {pages > 1 && (
                  <nav className="da-pages" aria-label="Pagination">
                    <button type="button" className="da-page da-page--nav" onClick={() => setPage(page - 1)} disabled={page <= 1} aria-label="Page précédente"><BI icon="chevron-left" /></button>
                    {from > 1 && <><button type="button" className="da-page" onClick={() => setPage(1)}>1</button>{from > 2 && <span className="da-page__trou">…</span>}</>}
                    {win.map(p => <button key={p} type="button" className="da-page" onClick={() => setPage(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>)}
                    {to < pages && <>{to < pages - 1 && <span className="da-page__trou">…</span>}<button type="button" className="da-page" onClick={() => setPage(pages)}>{pages}</button></>}
                    <button type="button" className="da-page da-page--nav" onClick={() => setPage(page + 1)} disabled={page >= pages} aria-label="Page suivante"><BI icon="chevron-right" /></button>
                  </nav>
                )}
                <ErpiaSelect value={String(pageLen)} onChange={e => setPageLen(Number(e.target.value))} title="Besoins par page">
                  <option value="10">10 / page</option>
                  <option value="25">25 / page</option>
                  <option value="50">50 / page</option>
                </ErpiaSelect>
              </div>
            </div>
          );
        })()}
      </section>
    </div>
  );
}

// ─── Tuile (vue Cartes) ──────────────────────────────────────────────────────
function BesoinTuile({ besoin, onSelect, onDelete, onPatch }) {
  const nb = besoin._count?.candidats ?? besoin.nb_candidats ?? 0;
  const age = ageInfo(besoin.created_at);
  const resp = nomResponsable(besoin.responsable);

  const titre = useInline(besoin.titre || '', v => v.trim() && onPatch(besoin.id, 'titre', v.trim()));
  const client = useInline(besoin.client || '', v => onPatch(besoin.id, 'client', v.trim()));
  const notes = useInline(besoin.notes_commercial || '', v => onPatch(besoin.id, 'notes_commercial', v));

  const inlineInput = { width: '100%', border: 'none', borderBottom: '2px solid var(--erp-da-accent)', outline: 'none', background: 'transparent', color: 'var(--erp-da-ink)', fontFamily: 'inherit' };

  return (
    <div className="da-tuile">
      <div className="da-tuile__corps">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
          {titre.editing ? (
            <input autoFocus value={titre.val} onChange={e => titre.setVal(e.target.value)} onBlur={titre.commit}
              onKeyDown={e => { if (e.key === 'Enter') titre.commit(); if (e.key === 'Escape') titre.cancel(); }}
              onClick={e => e.stopPropagation()} aria-label="Titre du besoin" style={{ ...inlineInput, fontSize: '15px', fontWeight: 600 }} />
          ) : (
            <h3 onClick={titre.start} title="Cliquer pour modifier" style={{ margin: 0, fontSize: '15px', fontWeight: 600, letterSpacing: 0, color: 'var(--erp-da-ink)', lineHeight: 1.3, cursor: 'text', flex: 1 }}>{besoin.titre}</h3>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
            <Priorite niveau={besoin.priorite} />
            <button type="button" className="da-carre da-carre--danger" onClick={e => { e.stopPropagation(); onDelete(besoin); }}
              aria-label={`Supprimer « ${besoin.titre} »`} title="Supprimer"><BI icon="trash3" /></button>
          </div>
        </div>

        {client.editing ? (
          <input autoFocus value={client.val} onChange={e => client.setVal(e.target.value)} onBlur={client.commit}
            onKeyDown={e => { if (e.key === 'Enter') client.commit(); if (e.key === 'Escape') client.cancel(); }}
            onClick={e => e.stopPropagation()} aria-label="Client du besoin" style={{ ...inlineInput, fontSize: '13px', color: 'var(--erp-da-text-2)', marginBottom: '12px' }} />
        ) : (
          <p onClick={client.start} title="Cliquer pour modifier" style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--erp-da-text-2)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'text' }}>
            <BI icon="building" style={{ fontSize: '12px' }} /> {besoin.client || <span style={{ fontStyle: 'italic', color: 'var(--erp-da-text-3)' }}>+ client</span>}
          </p>
        )}

        {notes.editing ? (
          <textarea autoFocus value={notes.val} onChange={e => notes.setVal(e.target.value)} onBlur={notes.commit}
            onKeyDown={e => { if (e.key === 'Escape') notes.cancel(); }} rows={3} onClick={e => e.stopPropagation()}
            placeholder="Notes : qualification, budget, contacts…" aria-label="Notes du commercial"
            style={{ width: '100%', fontSize: '12px', border: '1px solid var(--erp-da-line)', borderRadius: '8px', padding: '8px 10px', outline: 'none', background: 'var(--erp-da-surface)', color: 'var(--erp-da-ink)', resize: 'none', fontFamily: 'inherit', marginBottom: '12px', boxSizing: 'border-box' }} />
        ) : besoin.notes_commercial ? (
          <p onClick={notes.start} title="Cliquer pour modifier" style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--erp-da-text-2)', background: 'var(--erp-da-muted)', borderRadius: '8px', padding: '7px 10px', cursor: 'text', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            <BI icon="pencil-square" style={{ marginRight: '5px', color: 'var(--erp-da-text-3)' }} />{besoin.notes_commercial}
          </p>
        ) : (
          <button type="button" className="da-lien" onClick={notes.start} style={{ marginBottom: '12px' }}>+ Ajouter des notes</button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <PastilleStatut statut={besoin.statut} />
          <PastilleSourcingAuto sourcingAuto={besoin.sourcing_auto} />
          <span className="da-cellule--sourdine" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <BI icon="person" style={{ fontSize: '12px' }} /> {nb} candidat{nb > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="da-tuile__pied">
        <button type="button" className="da-btn da-btn--secondaire" onClick={() => onSelect(besoin)} style={{ minHeight: '30px', padding: '6px 12px' }}>
          <BI icon="eye" /> Voir le besoin
        </button>
        <div className="da-cellule--sourdine" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {age && <span className="da-age da-chiffre" data-niveau={age.niveau}><BI icon="clock-history" style={{ fontSize: '11px', marginRight: '3px' }} />{age.label}</span>}
          <span>{resp || '—'}</span>
          {besoin.recruteuse?.nom && <span title={`Recrutement : ${besoin.recruteuse.nom}`}><BI icon="person-check" /> {besoin.recruteuse.nom}</span>}
        </div>
      </div>
    </div>
  );
}
